/**
 * FMwvToolScriptConverter.ts - Main FMwvTool plugin
 * 
 * Orchestrates the conversion of FileMaker printable scripts to pastable objects
 * 
 * Workflow:
 * 1. Get printable text from clipboard or input field
 * 2. Convert to FileMaker XML (using FileMakerPrintableToXML)
 * 3. Copy XML to clipboard as pastable FM objects (using FileMakerXMLToClipboard)
 */

import type { Plugin, PluginContext } from '../../PluggrTypes';
import { FileMakerPrintableToXML } from './FileMakerPrintableToXML';
import { FileMakerXMLToClipboard } from './FileMakerXMLToClipboard';

export class FMwvToolScriptConverter {
  private converter: FileMakerPrintableToXML;
  private clipper: FileMakerXMLToClipboard;
  private context?: PluginContext;
  
  constructor(context?: PluginContext) {
    this.converter = new FileMakerPrintableToXML();
    this.clipper = new FileMakerXMLToClipboard();
    this.context = context;
  }
  
  /**
   * Convert printable text from clipboard to pastable FM objects
   */
  async convertFromClipboard(): Promise<{ success: boolean; xml?: string; error?: string }> {
    try {
      // Get text from clipboard
      const printableText = await this.clipper.getFromClipboard();
      
      if (!printableText || !printableText.trim()) {
        return { success: false, error: 'Clipboard is empty' };
      }
      
      // Convert to XML
      const xml = this.converter.convertToXML(printableText);
      
      // Copy XML back to clipboard as FM objects
      await this.clipper.copyToClipboard(xml);
      
      this.context?.log?.info('Successfully converted and copied FM script');
      
      return { success: true, xml };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      this.context?.log?.error('Conversion failed:', errorMsg);
      return { success: false, error: errorMsg };
    }
  }
  
  /**
   * Convert printable text (from input) to pastable FM objects
   */
  async convertFromText(printableText: string): Promise<{ success: boolean; xml?: string; error?: string }> {
    try {
      if (!printableText || !printableText.trim()) {
        return { success: false, error: 'Input text is empty' };
      }
      
      // Convert to XML
      const xml = this.converter.convertToXML(printableText);
      
      // Copy XML to clipboard as FM objects
      await this.clipper.copyToClipboard(xml);
      
      this.context?.log?.info('Successfully converted and copied FM script');
      
      return { success: true, xml };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      this.context?.log?.error('Conversion failed:', errorMsg);
      return { success: false, error: errorMsg };
    }
  }
  
  /**
   * Preview conversion without copying to clipboard
   */
  previewConversion(printableText: string): { success: boolean; xml?: string; error?: string } {
    try {
      if (!printableText || !printableText.trim()) {
        return { success: false, error: 'Input text is empty' };
      }
      
      const xml = this.converter.convertToXML(printableText);
      return { success: true, xml };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return { success: false, error: errorMsg };
    }
  }
}

// Plugin definition
export const fmwvToolScriptConverterPlugin: Plugin = {
  manifest: {
    id: 'fmwvtool-script-converter',
    name: 'FMwvTool Script Converter',
    version: '1.0.0',
    description: 'Convert FileMaker printable scripts to pastable objects',
    author: 'Trusted Associates',
    main: 'FMwvToolScriptConverter.ts',
    category: 'filemaker',
    tags: ['filemaker', 'script', 'converter', 'fmwvtool'],
    dependencies: {
      'filemaker-printable-to-xml': '^1.0.0',
      'filemaker-xml-to-clipboard': '^1.0.0'
    }
  },
  
  // Plugin lifecycle
  onload: async (context) => {
    const converter = new FMwvToolScriptConverter(context);
    
    // Register commands for FMwvTool UI
    context.registerCommand?.({
      id: 'convert-from-clipboard',
      name: 'Convert FM Script from Clipboard',
      description: 'Convert FileMaker printable script from clipboard to pastable objects',
      execute: async () => {
        const result = await converter.convertFromClipboard();
        
        if (result.success) {
          context.ui?.showNotification('Script converted and copied to clipboard!', 'success');
        } else {
          context.ui?.showNotification(`Conversion failed: ${result.error}`, 'error');
        }
        
        return result;
      }
    });
    
    context.registerCommand?.({
      id: 'convert-from-text',
      name: 'Convert FM Script from Text',
      description: 'Convert FileMaker printable script text to pastable objects',
      execute: async (printableText: string) => {
        const result = await converter.convertFromText(printableText);
        
        if (result.success) {
          context.ui?.showNotification('Script converted and copied to clipboard!', 'success');
        } else {
          context.ui?.showNotification(`Conversion failed: ${result.error}`, 'error');
        }
        
        return result;
      }
    });
    
    context.registerCommand?.({
      id: 'preview-conversion',
      name: 'Preview FM Script Conversion',
      description: 'Preview FileMaker script conversion without copying',
      execute: (printableText: string) => {
        return converter.previewConversion(printableText);
      }
    });
    
    context.log?.info('FMwvTool Script Converter plugin loaded');
  },
  
  onunload: async () => {
    // Cleanup if needed
  }
};
